/* tslint:disable:indent */
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UsageMonthlyReportService } from '../services/usage-monthly-report.service';
import { AuthService } from '../../../auth/auth.service';
import { Page } from '../../../model/page';
import { Subscription } from 'rxjs/Subscription';
import * as _ from 'lodash';
import { Title } from '@angular/platform-browser';
import { MatRadioChange } from '@angular/material';
import { ToastrService } from 'ngx-toastr';
import * as moment from 'moment-timezone';
import { CurrencyPipe } from '@angular/common';
import {fiveDecCompanies} from '../../../../environments/environment';

@Component({
	selector: 'app-usage-monthly',
	templateUrl: './usage-monthly.component.html',
	styleUrls: ['./usage-monthly.component.scss'],
	providers: [AuthService, UsageMonthlyReportService],
})
export class UsageMonthlyComponent implements OnInit, OnDestroy {
	public data: any;
	public displayReports: any;
	public cols: Array<any>;
	public initialCols: Array<any>;
	public filters: { [key: string]: any };
	private subscription: Subscription;
	public page: Page;
	public tooltipRows: Array<any>;

	public loading: boolean;
	public exportLoading: boolean;
	public reportLoaded: boolean;
	public isWorkspaceSelected: boolean;

	public windowSizeOptions = this.usageReportService.getWindowSizeOptions();
	public dataSourceOptions = this.usageReportService.getDataSourceOptions();

	public hideTooltip = false;

	protected readonly fiveDecCompanies = fiveDecCompanies;
	protected readonly parseInt = parseInt;

	constructor(
		private usageReportService: UsageMonthlyReportService,
		private authService: AuthService,
		private title: Title,
		private toast: ToastrService,
		private currencyPipe: CurrencyPipe
	) {
		this.title.setTitle('OES - Usage Analysis Monthly');
	}

	ngOnInit() {
		this.initValues();
		this.isWorkspaceSelected =
			this.authService.workspaceMasterCustomerID !== null;
		if (this.isWorkspaceSelected) {
			this.runReport();
		}
	}

	ngOnDestroy() {
		if (this.subscription) {
			this.subscription.unsubscribe();
		}
	}

	initValues() {
		this.filters = {
			windowSize: UsageMonthlyReportService.WINDOW_3M,
			dataSource: UsageMonthlyReportService.DATA_SOURCE_SD1,
		};
		this.data = {
			records: [],
			totalRecords: 0,
			dataSource: this.filters.dataSource,
			reportGenerated: '',
			windowDates: {
				p1Start: '',
				p1End: '',
				p2Start: '',
				p2End: '',
			},
			masterCustomer: {
				CU0_ENABLE_ORDER_ENTRY_USAGE_DATA_SYNCHRONIZATION: false,
				CU0_ALLOW_INVENTORY_MANAGEMENT: false,
			},
		};
		this.displayReports = {
			label: '',
			records: [],
			totalRecords: 0,
		};
		this.cols = [];
		this.page = new Page();
		this.page.size = 50;
		this.page.pageNumber = 0;
		this.loading = false;
		this.exportLoading = false;
		this.reportLoaded = false;
		this.tooltipRows = [];
		this.setInitialColumns();
	}

	runReport() {
		this.reportLoaded = false;
		this.page.pageNumber = 0;
		this.tooltipRows = [];
		this.getData();
	}

	getData() {
		this.loading = true;
		const filters = _.clone(this.filters);
		filters['tabularData'] = 1;
		filters['eagerLoad'] = 0;
		filters['perPage'] = this.page.size;
		filters['page'] = this.page.pageNumber;
		this.subscription = this.usageReportService.getReport(filters).subscribe(
			(result) => {
				this.setColumns();
				this.reportLoaded = true;
				const data = result.data;

				data['masterCustomer'][
					'CU0_ENABLE_ORDER_ENTRY_USAGE_DATA_SYNCHRONIZATION'
				] =
					Number(
						data['masterCustomer'][
							'CU0_ENABLE_ORDER_ENTRY_USAGE_DATA_SYNCHRONIZATION'
						]
					) === 1;
				data['masterCustomer']['CU0_ALLOW_INVENTORY_MANAGEMENT'] =
					Number(data['masterCustomer']['CU0_ALLOW_INVENTORY_MANAGEMENT']) ===
					1;
				this.dataSourceOptions = this.usageReportService.getDataSourceOptions(
					data['masterCustomer']['CU0_ALLOW_INVENTORY_MANAGEMENT'],
					data['masterCustomer'][
						'CU0_ENABLE_ORDER_ENTRY_USAGE_DATA_SYNCHRONIZATION'
					]
				);
				this.filters.dataSource = this.usageReportService.getDataSourceChoice(
					this.filters.dataSource,
					this.dataSourceOptions
				);
				this.data = data;
				this.setDisplayReportData();
				this.loading = false;
			},
			() => {
				this.loading = false;
				this.reportLoaded = false;
				this.toast.error('Request timed out', 'Service Error');
			}
		);
	}

	setInitialColumns() {
		this.initialCols = [
			{
				field: 'ITEM_ID',
				header: 'Item ID',
				visible: true,
				export: { visible: true, checked: true },
				width: 100,
				align: 'left',
			},
			{
				field: 'CUSTOMER_PART_NO',
				header: 'Cust. Part No.',
				visible: true,
				filter: true,
				sort: true,
				export: { visible: true, checked: true },
				width: 125,
				align: 'left',
			},
			{
				field: 'MD1_ITEM_DESC',
				header: 'Item Description',
				visible: true,
				export: { visible: true, checked: true },
				width: 200,
				align: 'left',
			},
			{
				field: 'RA1_SHIP_TO_NAME',
				header: 'Rack Name',
				visible: true,
				export: { visible: true, checked: true },
				width: 150,
				align: 'left',
			},
			{
				field: 'PL1_NAME',
				header: 'Plant Name',
				visible: true,
				export: { visible: true, checked: true },
				width: 150,
				align: 'left',
			},
		];
	}

	setColumns() {
		this.cols = [...this.initialCols];

		let startDate = moment().subtract(1, 'months');

		for (let index = 1; index <= this.filters.windowSize; index++) {
			const subfix = startDate.format('MM') + '/' + startDate.format('YY');

			this.cols.push(
				{
					field: 'QUANTITY_' + index,
					header: 'Qty ' + subfix,
					visible: true,
					export: { visible: true, checked: true },
					width: 100,
					align: 'right',
				},
				{
					field: 'HITS_' + index,
					header: 'Hits ' + subfix,
					visible: true,
					export: { visible: true, checked: true },
					width: 100,
					align: 'right',
				},
				{
					field: 'VALUE_' + index,
					header: 'Value ' + subfix,
					visible: true,
					export: { visible: true, checked: true },
					width: 100,
					align: 'right',
					format: 'currency',
				}
			);
			startDate = moment(startDate).subtract(1, 'months');
		}

		this.cols.push(
			{
				field: 'QUANTITY_TOTAL',
				header: 'Total Qty',
				visible: true,
				export: { visible: true, checked: true },
				width: 100,
				align: 'right',
			},
			{
				field: 'HITS_TOTAL',
				header: 'Total Hits',
				visible: true,
				export: { visible: true, checked: true },
				width: 100,
				align: 'right',
			},
			{
				field: 'VALUE_TOTAL',
				header: 'Total Value',
				visible: true,
				export: { visible: true, checked: true },
				width: 100,
				align: 'right',
				format: 'currency',
			}
		);

		this.cols.push(
			{
				field: 'QUANTITY_TARGET',
				header: 'Target Qty',
				visible: true,
				export: { visible: true, checked: true },
				width: 100,
				align: 'right',
			},
			{
				field: 'HITS_TARGET',
				header: 'Target Hits',
				visible: true,
				export: { visible: true, checked: true },
				width: 100,
				align: 'right',
			},
			{
				field: 'VALUE_TARGET',
				header: 'Target Value',
				visible: true,
				export: { visible: true, checked: true },
				width: 100,
				align: 'right',
				format: 'currency',
			}
		);
	}

	setDisplayReportData() {
		const data = this.data.records.slice();
		this.displayReports = {
			label: '',
			records: data,
			totalRecords: this.data.totalRecords,
		};

		this.getTooltipTextArray(data);
	}

	onPage(event) {
		this.page.size = event.rows;
		this.page.pageNumber = event.first / event.rows;
		this.getData();
	}

	onColumnResize(event) {
		const colField = event.element.attributes['data-field'].nodeValue;
		const delta = event.delta;

		const itemIndex = this.cols.findIndex((item) => item.field === colField);

		if (itemIndex !== -1 && this.cols[itemIndex].width + delta > 0) {
			this.initialCols[itemIndex].width = this.cols[itemIndex].width + delta;
		}
	}

	onSelectDataSource(event: MatRadioChange) {
		this.filters['dataSource'] = event.value;
		this.getData();
	}

	onExport() {
		this.exportLoading = true;
		const filters = _.clone(this.filters);
		filters['tabularData'] = 1;
		filters['eagerLoad'] = 0;
		this.usageReportService.export(filters).subscribe(
			(result) => {
				if (result.success) {
					setTimeout(() => {
						// delay to discourage hitting export multiple times
						this.exportLoading = false;
					}, 5000);
					this.toast.info(
						'Export is added to queue.' +
							' Please check the File Exports to download the file once it is ready!',
						'Update',
						{
							timeOut: 6000,
						}
					);
				} else {
					this.exportLoading = false;
					this.toast.error('Error in exporting data.');
				}
			},
			() => {
				this.exportLoading = false;
				window.dispatchEvent(new Event('resize'));
				this.toast.error('Request timed out', 'Service Error');
			}
		);
	}

	onHover(e) {
		const aux = e.target.offsetWidth < e.target.scrollWidth;
		this.hideTooltip = !aux;
	}

	getTooltipTextArray(data) {
		data.forEach((element) => {
			const newObj = JSON.parse(JSON.stringify(element));
			const currencyCols = this.cols.filter((el) => el.format === 'currency');

			currencyCols.forEach((col) => {
				newObj[col.field] = this.currencyPipe.transform(
					newObj[col.field],
					'USD',
					'symbol'
				);
			});

			this.tooltipRows.push(newObj);
		}, this);
	}
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/oes/usage-monthly/usage-monthly.component.ts